import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { formatZoneName, ZONE_ORDER, getZoneColor } from "../../utils/zoneUtils";

// ─── Types ─────────────────────────────────────────────────────────────────────
type ConsPeriod = "current-month" | "last-month" | "quarter" | "year" | "all";

interface ConsolidatedScore {
  avgScore: number | null;
  logCount: number;
  zone: string | null;
  status: string;
}
interface TimelinePoint { date: string; score: number; logCount: number; }
interface Category { id: number; name: string; }
interface DataPoint { date: string; catId: number; zone: string; }
interface JourneyPeriodPayload { dateRange: { from: string; to: string } | null; points: DataPoint[]; }
interface JourneyData { categories: Category[]; primary: JourneyPeriodPayload; compare: JourneyPeriodPayload | null; }

interface TooltipData {
  x: number;
  y: number;
  date: string;
  entries: { catName: string; color: string; zone: string; isCompare?: boolean; isEnsightly?: boolean }[];
}

// ─── Colors & Constants ────────────────────────────────────────────────────────
const CAT_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#3b82f6", "#f97316", "#1ecab8"];
const catColor = (idx: number) => CAT_COLORS[idx % CAT_COLORS.length];

const PERIODS: { key: ConsPeriod; label: string }[] = [
  { key: "current-month", label: "Current Month" },
  { key: "last-month",    label: "Last Month"    },
  { key: "quarter",       label: "Quarter"       },
  { key: "year",          label: "Year"          },
  { key: "all",           label: "All"           },
];

const fmtDate = (d: string) => {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
const fmtDateShort = (d: string) => {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// ─── Math Utils ────────────────────────────────────────────────────────────────
const splinePath = (pts: { x: number; y: number }[]): string => {
  if (pts.length === 0) return "";
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
  if (pts.length === 2) return `M${pts[0].x},${pts[0].y} L${pts[1].x},${pts[1].y}`;
  
  // Compute initial tangents at each point
  const m: number[] = [];
  for (let i = 0; i < pts.length; i++) {
    if (i === 0) {
      m.push((pts[1].y - pts[0].y) / (pts[1].x - pts[0].x));
    } else if (i === pts.length - 1) {
      m.push((pts[i].y - pts[i - 1].y) / (pts[i].x - pts[i - 1].x));
    } else {
      const s0 = (pts[i].y - pts[i - 1].y) / (pts[i].x - pts[i - 1].x);
      const s1 = (pts[i + 1].y - pts[i].y) / (pts[i + 1].x - pts[i].x);
      m.push(s0 * s1 <= 0 ? 0 : (s0 + s1) / 2);
    }
  }

  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    
    if (dx === 0 || dy === 0) {
      d += ` L${p1.x},${p1.y}`;
      continue;
    }
    
    let t0 = m[i];
    let t1 = m[i + 1];
    const slope = dy / dx;
    
    // Fritsch-Carlson constraint for monotonicity
    const maxT = 3 * Math.abs(slope);
    if (Math.abs(t0) > maxT) t0 = Math.sign(t0) * maxT;
    if (Math.abs(t1) > maxT) t1 = Math.sign(t1) * maxT;
    
    const cp1x = p0.x + dx / 3;
    const cp1y = p0.y + t0 * (dx / 3);
    const cp2x = p1.x - dx / 3;
    const cp2y = p1.y - t1 * (dx / 3);
    
    d += ` C${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p1.x.toFixed(2)},${p1.y.toFixed(2)}`;
  }
  return d;
};

// SVG Chart Constants
const BAND_H  = 55;    
const Y_LABEL = 140;   
const X_PAD   = 32;
const TOP_PAD = 16;
const BOT_PAD = 56;    

// Gauge Constants
const GAUGE_R      = 60;
const GAUGE_STROKE = 12;
const GAUGE_CX     = 80;
const GAUGE_CY     = 80;
const FULL_CIRC    = 2 * Math.PI * GAUGE_R;
const GAUGE_SEGMENTS = [...ZONE_ORDER].reverse();
const SEG_ANGLE = FULL_CIRC / GAUGE_SEGMENTS.length;

// ─── Gauge Component ───────────────────────────────────────────────────────────
interface GaugeProps { score: number | null; zone: string | null; logCount: number; status: string; compareScore?: number | null; }
const GaugeChart: React.FC<GaugeProps> = ({ score, zone, logCount, status, compareScore }) => {
  const zc       = getZoneColor(zone);
  const fraction = score != null ? Math.max(0, Math.min(1, (score - 1) / 6)) : 0;
  const dash     = fraction * FULL_CIRC;
  return (
    <div className="flex flex-col items-center">
      <svg viewBox={`0 0 ${GAUGE_CX * 2} ${GAUGE_CY * 2}`} width="100%" style={{ maxWidth: 160, display: "block" }}>
        <defs>
          <filter id="gauge-glow"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
        </defs>
        {GAUGE_SEGMENTS.map((band, i) => (
          <circle key={band} cx={GAUGE_CX} cy={GAUGE_CY} r={GAUGE_R} fill="none" stroke={getZoneColor(band).color} strokeOpacity={0.22} strokeWidth={GAUGE_STROKE} strokeDasharray={`${SEG_ANGLE} ${FULL_CIRC - SEG_ANGLE}`} strokeDashoffset={-(i * SEG_ANGLE - FULL_CIRC / 4)} strokeLinecap="butt" />
        ))}
        <circle cx={GAUGE_CX} cy={GAUGE_CY} r={GAUGE_R} fill="none" stroke={zc.color} strokeWidth={GAUGE_STROKE} strokeDasharray={`${dash} ${FULL_CIRC - dash}`} strokeDashoffset={FULL_CIRC / 4} strokeLinecap="butt" style={{ transition: "stroke-dasharray 0.8s cubic-bezier(0.4,0,0.2,1), stroke 0.4s" }} filter="url(#gauge-glow)" />
        <circle cx={GAUGE_CX} cy={GAUGE_CY} r={GAUGE_R - GAUGE_STROKE - 4} fill="#fff" />
        {score != null ? (
          <>
            <text x={GAUGE_CX} y={GAUGE_CY - 10} textAnchor="middle" fontSize={22} fontWeight={800} fill={zc.color}>{score.toFixed(1)}</text>
            <text x={GAUGE_CX} y={GAUGE_CY + 5} textAnchor="middle" fontSize={10} fontWeight={600} fill="#9ca3af">/ 7.0</text>
            <text x={GAUGE_CX} y={GAUGE_CY + 18} textAnchor="middle" fontSize={9} fontWeight={700} fill={zc.textColor}>{zone ?? "—"}</text>
          </>
        ) : (
          <text x={GAUGE_CX} y={GAUGE_CY + 6} textAnchor="middle" fontSize={11} fill="#9ca3af" fontWeight={600}>No data</text>
        )}
      </svg>
      {compareScore != null && score != null && (
        <div className="mt-2 -mb-1 flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border border-gray-100 bg-gray-50 text-gray-600">
          <span className="text-gray-400 font-medium tracking-wide">COMPARE</span>
          {score > compareScore ? <span className="text-emerald-500">↑ +{(score - compareScore).toFixed(1)}</span> : score < compareScore ? <span className="text-rose-500">↓ -{(compareScore - score).toFixed(1)}</span> : <span className="text-gray-400">— No change</span>}
        </div>
      )}
      {score != null && <div className="mt-2 px-5 py-1.5 rounded-full text-sm font-bold" style={{ background: zc.color, color: "#fff" }}>{status}</div>}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

interface Props {
  studentId: number | string;
  primaryPeriod: ConsPeriod;
  comparePeriod?: ConsPeriod;
  showCompare?: boolean;
  onTimelineClick?: (date: string) => void;
  primaryStartDate?: string;
  primaryEndDate?: string;
  compareStartDate?: string;
  compareEndDate?: string;
}

const ConsolidatedView: React.FC<Props> = ({ 
  studentId, primaryPeriod, comparePeriod, showCompare, onTimelineClick,
  primaryStartDate, primaryEndDate, compareStartDate, compareEndDate
}) => {
  // SVG Refs
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Data States
  const [score, setScore] = useState<ConsolidatedScore | null>(null);
  const [compareScore, setCompareScore] = useState<ConsolidatedScore | null>(null);
  const [ensightlyTimeline, setEnsightlyTimeline] = useState<TimelinePoint[]>([]);
  const [compareEnsightlyTimeline, setCompareEnsightlyTimeline] = useState<TimelinePoint[]>([]);
  const [journeyData, setJourneyData] = useState<JourneyData | null>(null);
  const [dbZones, setDbZones] = useState<{ name: string; color: string; textColor: string }[]>([]);

  // UI States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCatIds, setActiveCatIds] = useState<Set<number>>(new Set());
  const [showEnsightly, setShowEnsightly] = useState(true);
  const [period, setPeriod] = useState<ConsPeriod>(primaryPeriod ?? "all");
  const [svgWidth, setSvgWidth] = useState(800);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);

  useEffect(() => { setPeriod(primaryPeriod ?? "all"); }, [primaryPeriod]);

  useEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => {
      const w = containerRef.current?.clientWidth || 0;
      if (w > 0) setSvgWidth(Math.max(360, w));
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [loading, journeyData, ensightlyTimeline]);

  const fetchAll = useCallback(async () => {
    if (!studentId) return;
    try {
      setLoading(true); setError(null);
      const params: any = { period, ...(primaryStartDate && { startDate: primaryStartDate }), ...(primaryEndDate && { endDate: primaryEndDate }) };
      const jParams: any = { primary: period, ...(primaryStartDate && { primaryStartDate }), ...(primaryEndDate && { primaryEndDate }) };
      
      const promises = [
        axios.get(`${API_BASE_URL}/student-mood-logs/consolidated/${studentId}`, { params }),
        axios.get(`${API_BASE_URL}/student-mood-logs/ensightly/${studentId}`, { params }),
        axios.get(`${API_BASE_URL}/student-mood-logs/journey/${studentId}`, { params: jParams }),
        axios.get(`${API_BASE_URL}/zones`),
      ];
      
      if (showCompare && comparePeriod) {
        const cParams: any = { period: comparePeriod, ...(compareStartDate && { startDate: compareStartDate }), ...(compareEndDate && { endDate: compareEndDate }) };
        const jCParams: any = { ...jParams, compare: comparePeriod, ...(compareStartDate && { compareStartDate }), ...(compareEndDate && { compareEndDate }) };
        
        promises.push(
          axios.get(`${API_BASE_URL}/student-mood-logs/consolidated/${studentId}`, { params: cParams }),
          axios.get(`${API_BASE_URL}/student-mood-logs/ensightly/${studentId}`, { params: cParams }),
          axios.get(`${API_BASE_URL}/student-mood-logs/journey/${studentId}`, { params: jCParams })
        );
      }

      const results = await Promise.all(promises);
      setScore(results[0].data);
      setEnsightlyTimeline(results[1].data.timeline ?? []);
      setJourneyData(results[2].data);
      
      const validZones = (results[3].data || []).filter((z: any) => z.status !== false && z.name !== "Light Red" && z.name !== "Orange Zone");
      validZones.sort((a: any, b: any) => {
        let ai = ZONE_ORDER.indexOf(formatZoneName(a.name));
        let bi = ZONE_ORDER.indexOf(formatZoneName(b.name));
        if (ai === -1) ai = 999;
        if (bi === -1) bi = 999;
        return ai - bi;
      });
      setDbZones(validZones);
      
      if (showCompare && comparePeriod && results.length > 4) {
        setCompareScore(results[4].data);
        setCompareEnsightlyTimeline(results[5].data.timeline ?? []);
        const rawJourney = results[6].data;
        if (rawJourney?.primary?.points) rawJourney.primary.points.forEach((p: any) => { if (p.zone) p.zone = formatZoneName(p.zone); });
        if (rawJourney?.compare?.points) rawJourney.compare.points.forEach((p: any) => { if (p.zone) p.zone = formatZoneName(p.zone); });
        setJourneyData(rawJourney);
      } else {
        setCompareScore(null);
        setCompareEnsightlyTimeline([]);
        const rawJourney = results[2].data;
        if (rawJourney?.primary?.points) rawJourney.primary.points.forEach((p: any) => { if (p.zone) p.zone = formatZoneName(p.zone); });
        setJourneyData(rawJourney);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to load consolidated data");
    } finally {
      setLoading(false);
    }
  }, [studentId, period, showCompare, comparePeriod, primaryStartDate, primaryEndDate, compareStartDate, compareEndDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Derived chart math ─────────────────────────────────────────────────────
  const ZONES = dbZones;
  const ZONE_Y = Object.fromEntries(ZONES.map((z, i) => [z.name, i]));
  const ZONE_COUNT = ZONES.length || 1;
  const CHART_H = ZONE_COUNT * BAND_H + TOP_PAD + BOT_PAD;

  const yForZone = useCallback((zone: string): number => {
    const idx = ZONE_Y[zone] ?? Math.max(0, ZONE_COUNT - 1);
    return TOP_PAD + idx * BAND_H + BAND_H / 2;
  }, [ZONE_Y, ZONE_COUNT]);

  const yForScore = useCallback((s: number): number => {
    const clamped = Math.max(1, Math.min(7, s));
    const fraction = (clamped - 1) / 6;
    return TOP_PAD + (1 - fraction) * (ZONE_COUNT * BAND_H);
  }, [ZONE_COUNT]);

  const INNER_X_PAD = 32;
  const plotWidth = svgWidth - Y_LABEL - X_PAD - (INNER_X_PAD * 2);

  // Merge all dates from Ensightly and Journey points
  const allDates = useMemo(() => {
    const set = new Set<string>();
    ensightlyTimeline.forEach(p => set.add(p.date));
    compareEnsightlyTimeline.forEach(p => set.add(p.date));
    journeyData?.primary?.points.forEach(p => set.add(p.date));
    journeyData?.compare?.points.forEach(p => set.add(p.date));
    return Array.from(set).sort();
  }, [ensightlyTimeline, compareEnsightlyTimeline, journeyData]);

  const xForDate = useCallback((date: string) => {
    const idx = allDates.indexOf(date);
    if (idx < 0 || allDates.length < 2) return Y_LABEL + INNER_X_PAD + plotWidth / 2;
    return Y_LABEL + INNER_X_PAD + (idx / (allDates.length - 1)) * plotWidth;
  }, [allDates, plotWidth]);

  // Build category series
  const buildCatSeries = useCallback((points: DataPoint[]) => {
    const map = new Map<number, Array<{ x: number; y: number; zone: string; date: string }>>();
    for (const p of points) {
      if (!activeCatIds.has(p.catId)) continue;
      if (!map.has(p.catId)) map.set(p.catId, []);
      map.get(p.catId)!.push({ x: xForDate(p.date), y: yForZone(p.zone), zone: p.zone, date: p.date });
    }
    return map;
  }, [activeCatIds, xForDate, yForZone]);

  const primarySeries = useMemo(() => journeyData ? buildCatSeries(journeyData.primary.points) : new Map<number, { x: number; y: number; zone: string; date: string }[]>(), [journeyData, buildCatSeries]);
  const compareSeries = useMemo(() => journeyData?.compare ? buildCatSeries(journeyData.compare.points) : new Map<number, { x: number; y: number; zone: string; date: string }[]>(), [journeyData, buildCatSeries]);

  const xTicks = useMemo(() => {
    if (!allDates.length) return [];
    const step = Math.max(1, Math.floor(allDates.length / 8));
    return allDates.filter((_, i) => {
      if (i === allDates.length - 1) return true; // Always show the latest date
      if (i % step === 0) {
        // Drop this tick if it's too close to the end date to prevent overlapping
        if (allDates.length - 1 - i < step * 0.7) return false;
        return true;
      }
      return false;
    });
  }, [allDates]);

  // Hover Interactions
  const handleSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    if (mouseX < Y_LABEL || mouseX > svgWidth - X_PAD) { setHoverX(null); setTooltip(null); return; }
    setHoverX(mouseX);
    if (allDates.length === 0) return;
    
    const idx = Math.round(((mouseX - Y_LABEL - INNER_X_PAD) / plotWidth) * (allDates.length - 1));
    const date = allDates[Math.max(0, Math.min(idx, allDates.length - 1))];
    const snapX = xForDate(date);
    const entries: TooltipData["entries"] = [];

    if (showEnsightly) {
      const ePt = ensightlyTimeline.find(p => p.date === date);
      if (ePt) {
        const c = Math.max(1, Math.min(7, ePt.score));
        const zIdx = Math.max(0, Math.min(ZONES.length - 1, Math.floor((7 - c) / (6 / ZONES.length))));
        entries.push({ catName: "Ensightly Score", color: "#1ecab8", zone: ZONES[zIdx]?.name || "N/A", isEnsightly: true });
      }
      
      if (showCompare) {
        const cePt = compareEnsightlyTimeline.find(p => p.date === date);
        if (cePt) {
          const c = Math.max(1, Math.min(7, cePt.score));
          const zIdx = Math.max(0, Math.min(ZONES.length - 1, Math.floor((7 - c) / (6 / ZONES.length))));
          entries.push({ catName: "Ensightly Score", color: "#9ca3af", zone: ZONES[zIdx]?.name || "N/A", isCompare: true, isEnsightly: true });
        }
      }
    }

    if (journeyData?.categories) {
      journeyData.categories.forEach((cat, ci) => {
        if (!activeCatIds.has(cat.id)) return;
        const color = catColor(ci);
        const pt = primarySeries.get(cat.id)?.find(p => p.date === date);
        if (pt) entries.push({ catName: cat.name, color, zone: pt.zone });
        
        if (showCompare) {
          const cpt = compareSeries.get(cat.id)?.find(p => p.date === date);
          if (cpt) entries.push({ catName: cat.name, color, zone: cpt.zone, isCompare: true });
        }
      });
    }

    setTooltip({ x: snapX, y: e.clientY - rect.top, date, entries });
  };
  const handleSvgMouseLeave = () => { setHoverX(null); setTooltip(null); };

  // Building Ensightly Line paths (with gaps for nulls)
  const buildEnsightlyPath = (tl: TimelinePoint[]) => {
    if (!tl.length) return { lineD: "", areaD: "" };
    
    const data: { date: string; score: number | null }[] = [];
    const start = new Date(tl[0].date + "T12:00:00Z");
    const end = new Date(tl[tl.length - 1].date + "T12:00:00Z");
    let curr = start;
    while (curr <= end) {
      const dStr = curr.toISOString().split("T")[0];
      const found = tl.find(p => p.date === dStr);
      if (allDates.includes(dStr)) {
        data.push({ date: dStr, score: found ? found.score : null });
      }
      curr.setUTCDate(curr.getUTCDate() + 1);
    }

    let lineD = ""; let areaD = "";
    let currentChunk: { x: number; y: number }[] = [];

    const processChunk = (chunk: { x: number; y: number }[]) => {
      if (chunk.length === 0) return;
      const path = splinePath(chunk);
      lineD += path + " ";
      
      const first = chunk[0];
      const last = chunk[chunk.length - 1];
      const botY = (TOP_PAD + ZONE_COUNT * BAND_H).toFixed(1);
      
      areaD += path + ` L${last.x.toFixed(1)},${botY} L${first.x.toFixed(1)},${botY} Z `;
    };

    data.forEach(p => {
      if (p.score !== null) {
        currentChunk.push({ x: xForDate(p.date), y: yForScore(p.score) });
      } else {
        processChunk(currentChunk);
        currentChunk = [];
      }
    });
    processChunk(currentChunk);

    return { lineD, areaD };
  };

  const { lineD: ensightlyLineD, areaD: ensightlyAreaD } = buildEnsightlyPath(ensightlyTimeline);
  const { lineD: compareEnsightlyLineD } = buildEnsightlyPath(compareEnsightlyTimeline);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="w-9 h-9 border-[3px] border-emerald-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-gray-400 font-medium">Loading consolidated data…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <p className="text-sm font-semibold text-red-500">{error}</p>
      <button onClick={fetchAll} className="text-xs text-emerald-600 font-bold hover:underline">Retry</button>
    </div>
  );

  const categories = journeyData?.categories || [];
  const toggleCat = (id: number) =>
    setActiveCatIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div className="w-full flex flex-col xl:flex-row gap-6">
      
      {/* ── Left Sidebar: Overview & Gauge ─────────────────────────────────── */}
      <div className="w-full xl:w-[260px] flex flex-col gap-6 shrink-0">
        <div className="bg-white rounded-[24px] border border-gray-100 p-5 flex flex-col shadow-[0_2px_20px_rgba(0,0,0,0.02)] h-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: "linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)" }}>
              <svg style={{width:18,height:18}} fill="none" viewBox="0 0 24 24" stroke="white"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
            </div>
            <div>
              <h3 className="text-[15px] font-extrabold text-gray-900 leading-tight tracking-tight">Consolidated View</h3>
              <p className="text-[10px] text-gray-500 mt-0.5 font-bold tracking-wide uppercase">Zone Trend & Score</p>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col justify-center items-center py-5 border-t border-gray-50">
            <div className="mb-6 text-center">
              <p className="text-[11px] font-bold text-gray-800 uppercase tracking-widest">Zone · Self View</p>
              <p className="text-[10px] text-gray-400 mt-1 font-medium">Consolidated Emotional State</p>
            </div>
            <GaugeChart
              score={score?.avgScore ?? null}
              zone={score?.zone ?? null}
              logCount={score?.logCount ?? 0}
              status={score?.status ?? ""}
              compareScore={compareScore?.avgScore ?? null}
            />
          </div>
        </div>
      </div>

      {/* ── Right Area: Main Graph ─────────────────────────────────────────── */}
      <div className="flex-1 min-w-0 flex flex-col bg-white rounded-[24px] border border-gray-100 shadow-[0_2px_20px_rgba(0,0,0,0.02)] overflow-hidden">
        
        {/* Graph Header: Controls */}
        <div className="px-6 py-5 border-b border-gray-50 bg-[#fafafa]/50 flex flex-wrap items-center justify-between gap-4">
          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">Metrics Displayed</span>
          
          {/* Categories / Toggles */}
          <div className="flex flex-wrap gap-2 items-center">
            <button 
              onClick={() => setShowEnsightly(p => !p)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold border transition-all shadow-sm"
              style={showEnsightly ? { background: "#1ecab81a", color: "#0f766e", borderColor: "#1ecab8" } : { background: "#f9fafb", color: "#9ca3af", borderColor: "#f3f4f6" }}
            >
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: showEnsightly ? "#1ecab8" : "#d1d5db" }} />
              Ensightly Score
            </button>
            <div className="w-px h-5 bg-gray-200 flex-shrink-0 mx-1" />
            {categories.map((cat, i) => {
              const active = activeCatIds.has(cat.id);
              const color  = catColor(i);
              return (
                <button
                  key={cat.id} onClick={() => toggleCat(cat.id)} title={cat.name}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all shadow-sm"
                  style={active ? { background: color + "1a", color, borderColor: color } : { background: "#f9fafb", color: "#9ca3af", borderColor: "#f3f4f6" }}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: active ? color : "#d1d5db" }} />
                  {cat.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Graph Body */}
        <div className="p-6 bg-white flex-1 relative min-w-0 overflow-hidden">
          <div ref={containerRef} className="relative w-full h-full min-w-0">
            <svg 
              width={svgWidth} height={CHART_H} className="block cursor-crosshair" style={{ width: "100%" }}
              ref={svgRef} onMouseMove={handleSvgMouseMove} onMouseLeave={handleSvgMouseLeave}
            >
              <defs>
                <linearGradient id="tl-area-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1ecab8" stopOpacity="0.15" /><stop offset="100%" stopColor="#1ecab8" stopOpacity="0.0" /></linearGradient>
                <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="6" stdDeviation="6" floodColor="#1ecab8" floodOpacity="0.35"/>
                </filter>
              </defs>

              {ZONES.map((z, i) => {
                const zColor = getZoneColor(z.name);
                const yTop = TOP_PAD + i * BAND_H;
                const shortName = z.name === "Dark Red" ? "Dark Red" : z.name.replace(" Zone", "");
                return (
                  <g key={z.name}>
                    <rect x={0} y={yTop} width={svgWidth} height={BAND_H} fill={i % 2 === 0 ? "#ffffff" : "#f8fafc"} />
                    <rect x={0} y={yTop} width={4} height={BAND_H} fill={zColor.color} opacity={0.9} />
                    
                    {i > 0 && <line x1={0} y1={yTop} x2={svgWidth} y2={yTop} stroke="#f1f5f9" strokeWidth={1.5} />}
                    
                    {/* Zone Pill */}
                    <rect x={24} y={yTop + BAND_H / 2 - 14} width={100} height={28} rx={14} fill={zColor.color} opacity={0.12} />
                    <circle cx={38} cy={yTop + BAND_H / 2} r={5} fill={zColor.color} />
                    <text x={50} y={yTop + BAND_H / 2} textAnchor="start" fontSize={11.5} fontWeight={800} fill={zColor.color} dominantBaseline="middle" letterSpacing={0.5}>{shortName}</text>
                  </g>
                );
              })}

              {hoverX != null && <line x1={hoverX} y1={TOP_PAD} x2={hoverX} y2={CHART_H - BOT_PAD} stroke="rgba(30,202,184,0.4)" strokeWidth={2} strokeDasharray="4,4" />}

              {/* Ensightly Area */}
              {showEnsightly && <path d={ensightlyAreaD} fill="url(#tl-area-grad)" />}

              {/* Category Lines */}
              {Array.from(primarySeries.entries()).map(([catId, pts]) => {
                const ci = categories.findIndex(c => c.id === catId);
                return <path key={`p-${catId}`} d={splinePath(pts)} fill="none" stroke={catColor(ci)} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.8} />;
              })}
              {showCompare && Array.from(compareSeries.entries()).map(([catId, pts]) => {
                const ci = categories.findIndex(c => c.id === catId);
                return <path key={`c-${catId}`} d={splinePath(pts)} fill="none" stroke={catColor(ci)} strokeWidth={2} strokeDasharray="6,6" strokeLinecap="round" opacity={0.4} />;
              })}

              {/* Ensightly Lines */}
              {showEnsightly && ensightlyLineD && <path d={ensightlyLineD} fill="none" stroke="#1ecab8" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round" filter="url(#lineGlow)" />}
              {showCompare && showEnsightly && compareEnsightlyLineD && <path d={compareEnsightlyLineD} fill="none" stroke="#94a3b8" strokeWidth={3} strokeDasharray="6,6" strokeLinecap="round" opacity={0.8} />}

              {/* Category Markers */}
              {Array.from(primarySeries.entries()).map(([catId, pts]) => {
                 const ci = categories.findIndex(c => c.id === catId);
                 return pts.map((pt, i) => <circle key={`${catId}-${i}`} cx={pt.x} cy={pt.y} r={4.5} fill="#fff" stroke={catColor(ci)} strokeWidth={2.5} />);
              })}

              <line x1={Y_LABEL} y1={CHART_H - BOT_PAD} x2={svgWidth} y2={CHART_H - BOT_PAD} stroke="#e2e8f0" strokeWidth={2} />
              {xTicks.map(d => <line key={`vg-${d}`} x1={xForDate(d)} y1={TOP_PAD} x2={xForDate(d)} y2={CHART_H - BOT_PAD} stroke="#f1f5f9" strokeWidth={1.5} strokeDasharray="4,4" />)}
              {xTicks.map(d => <text key={d} x={xForDate(d)} y={CHART_H - BOT_PAD + 28} textAnchor="middle" fontSize={12} fill="#64748b" fontWeight={700}>{fmtDateShort(d)}</text>)}
            </svg>

            {/* Tooltip */}
            {tooltip && tooltip.entries.length > 0 && (
              <div className="absolute pointer-events-none z-40 rounded-[16px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-white/40 overflow-hidden"
                style={{ background: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)", left: tooltip.x, transform: tooltip.x + Y_LABEL > svgWidth / 2 ? "translateX(calc(-100% - 20px))" : "translateX(20px)", top: Math.max(8, tooltip.y - 80), minWidth: 200, maxWidth: 280 }}
              >
                <div className="px-4 py-3 border-b border-gray-100/80 flex items-center gap-2 bg-gray-50/50">
                  <span className="text-[12px] font-extrabold text-gray-800 tracking-tight">{fmtDate(tooltip.date)}</span>
                </div>
                <div className="px-4 py-3 space-y-2.5">
                  {tooltip.entries.map((e, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 shadow-sm" style={{ background: e.isCompare ? "transparent" : e.color, border: `2.5px solid ${e.color}` }} />
                      <span className="text-[11px] font-bold text-gray-600 flex-1 truncate">{e.catName}{e.isCompare ? <span className="text-gray-400 ml-1 font-semibold">(Compare)</span> : null}</span>
                      <span className="text-[11px] font-black px-2.5 py-0.5 rounded-lg" style={{ color: e.color, background: e.color + "1a" }}>{e.isEnsightly && !e.zone.includes("Zone") && e.zone !== "N/A" ? e.zone.replace("Dark ", "D. ") : e.zone.replace(" Zone", "")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="px-6 py-4 border-t border-gray-50 bg-[#fafafa]/50">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {ZONES.map(z => {
              const shortName = z.name === "Dark Red" ? "Dark Red" : z.name.replace(" Zone", "");
              return (
                <div key={z.name} className="flex items-center gap-2.5">
                  <span className="w-3.5 h-3.5 rounded-full flex-shrink-0 shadow-sm" style={{ background: getZoneColor(z.name).color }} />
                  <span className="text-[11px] text-gray-500 font-bold tracking-widest uppercase">{shortName}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedView;
